import React, { Fragment, useRef } from 'react'
import {
	Button,
	Toolbar,
	TextInput,
	Select,
	MenuItem,
	Form,
	Flexgrid,
	FlexgridItem,
	Container,
} from '@wf-wfria/pioneer-core'
import { FormattedMessage } from 'react-intl'
import { ModalDialog } from './ModalDialog'
import { onFocusUserIdInput } from './RegistrationForm'
import { SystemMessages } from './SystemMessages'
import {
	DOCEX,
	WFORMS,
	RFI,
	ONBOARD,
	SBA,
	WFINFOLEASE,
	PLINFOLEASE,
	RICOH,
	DLO,
	COBO,
	EASYCONNECT,
	FORDPRO,
} from '../businessSegmentBrandIds'

const firstNameRef = React.createRef()
const lastNameRef = React.createRef()
const companyRef = React.createRef()
const phoneRef = React.createRef()
const userIdRef = React.createRef()

export function CreateUserIdFormContent({
	inputFields,
	deviceType,
	messages,
	formatMessage,
	userIdClass,
	mode,
	languagePreference,
	showLanguageDropDown,
	onUpdateInput,
	onFocusInput,
	onDeviceTypeChange,
	onLanguagePreferenceChange,
	onCustomTextInputValidator,
}) {
	function onFocus(event) {
		const targetId = event.target.id
		onFocusInput(targetId)
	}

	function onChange({ id, value }) {
		onUpdateInput(id, value)
	}

	function onTextInputValidator(elementId, inputValue) {
		return onCustomTextInputValidator(elementId, inputValue, formatMessage)
	}

	return (
		<Flexgrid columnGap={1} rowGap={1}>
			<FlexgridItem span={10}>
				<SystemMessages messages={messages} />
			</FlexgridItem>
			<FlexgridItem span={12} style={{ marginTop: '0px' }}>
				<Container display="block">
					<Flexgrid>
						<FlexgridItem span={8}>
							<CreateUserIdInputNonCC
								mode={mode}
								formatMessage={formatMessage}
								inputFields={inputFields}
								deviceType={deviceType}
								userIdClass={userIdClass}
								languagePreference={languagePreference}
								showLanguageDropDown={showLanguageDropDown}
								onFocus={onFocus}
								onChange={onChange}
								onDeviceTypeChange={onDeviceTypeChange}
								onLanguagePreferenceChange={onLanguagePreferenceChange}
								onTextInputValidator={onTextInputValidator}
							/>
						</FlexgridItem>
						<FlexgridItem span={4} style={{ marginTop: '28px' }}>
							<div>
								<h3>
									<strong>
										<FormattedMessage id="create-user-id-form-content.user-id.requirements" />
									</strong>
								</h3>
								<span>
									<strong>
										<FormattedMessage id="create-user-id-form-content.user-id.requirements.must-contain.header-label" />
									</strong>
								</span>
								<ul>
									<li>
										<FormattedMessage id="create-user-id-form-content.user-id.requirements.must-contain.character-range" />
									</li>
								</ul>
								<span>
									<strong>
										<FormattedMessage id="create-user-id-form-content.user-id.requirements.cannot-contain.header-label" />
									</strong>
								</span>
								<ul>
									<li>
										<FormattedMessage id="create-user-id-form-content.user-id.requirements.cannot-contain.spaces" />
									</li>
									<li>
										<FormattedMessage id="create-user-id-form-content.user-id.requirements.cannot-contain.special-characters" />
									</li>
								</ul>
							</div>
						</FlexgridItem>
					</Flexgrid>
				</Container>
			</FlexgridItem>
		</Flexgrid>
	)
}

export function GetCompany({
	mode,
	formatMessage,
	company,
	onChange,
	onTextInputValidator,
	valueMissing,
}) {
	function getCustomValidatorKey() {
		if (mode === RICOH) {
			return 'companyRicoh'
		}
		return 'company'
	}

	return (
		<TextInput
			aria-label={formatMessage({
				id: 'create-user-id-form-content.company.aria-label',
			})}
			label={formatMessage({
				id: 'create-user-id-form-content.company.label',
			})}
			id="company"
			name="company"
			value={company.value}
			onValueChange={onChange}
			ref={companyRef}
			aria-required={true}
			required={true}
			customValidators={[
				(value) => onTextInputValidator(getCustomValidatorKey(), value),
			]}
			nativeValidationMessages={{
				valueMissing,
			}}
		/>
	)
}

export function CreateUserIdInputNonCC({
	mode,
	formatMessage,
	inputFields,
	deviceType,
	languagePreference,
	showLanguageDropDown,
	onBlur,
	onChange,
	onDeviceTypeChange,
	onLanguagePreferenceChange,
	onTextInputValidator,
}) {
	if (
		mode === DOCEX ||
		mode === WFORMS ||
		mode === ONBOARD ||
		mode === SBA ||
		mode === RFI ||
		mode === DLO ||
		mode === COBO ||
		mode === EASYCONNECT
	) {
		return <Fragment />
	}
	const valueMissing = formatMessage({ id: 'required-field.empty.error.label' })
	const {
		newUserIdNotCC,
		firstName,
		middleInitial,
		lastName,
		title,
		company,
		phone,
		extension,
	} = inputFields
	const extensionDisabled = deviceType !== 'landline'

	function invokeDeviceTypeChange({ value }) {
		onDeviceTypeChange(value)
	}

	function invokeLanguagePreferenceChange(event) {
		const languagePreference = event.target.value
		onLanguagePreferenceChange(languagePreference)
	}

	return (
		<Flexgrid columnGap={3} rowGap={2}>
			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<TextInput
					aria-label={formatMessage({
						id: 'create-user-id-form-content.first-name.aria-label',
					})}
					label={formatMessage({
						id: 'create-user-id-form-content.first-name.label',
					})}
					id="firstName"
					name="firstName"
					type="text"
					value={firstName.value}
					onValueChange={onChange}
					aria-required={true}
					required={true}
					ref={firstNameRef}
					customValidators={[
						(value) => onTextInputValidator('firstName', value),
					]}
					nativeValidationMessages={{
						valueMissing,
					}}
				/>
			</FlexgridItem>
			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<TextInput
					aria-label={formatMessage({
						id: 'create-user-id-form-content.middle-initial.aria-label',
					})}
					label={formatMessage({
						id: 'create-user-id-form-content.middle-initial.label',
					})}
					id="middleInitial"
					name="middleInitial"
					value={middleInitial.value}
					onValueChange={onChange}
					customValidators={[
						(value) => onTextInputValidator('middleInitial', value),
					]}
				/>
			</FlexgridItem>
			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<TextInput
					aria-label={formatMessage({
						id: 'create-user-id-form-content.last-name.aria-label',
					})}
					label={formatMessage({
						id: 'create-user-id-form-content.last-name.label',
					})}
					id="lastName"
					name="lastName"
					value={lastName.value}
					onValueChange={onChange}
					aria-required={true}
					required={true}
					ref={lastNameRef}
					customValidators={[
						(value) => onTextInputValidator('lastName', value),
					]}
					nativeValidationMessages={{
						valueMissing,
					}}
				/>
			</FlexgridItem>
			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<TextInput
					aria-label={formatMessage({
						id: 'create-user-id-form-content.title.aria-label',
					})}
					label={formatMessage({
						id: 'create-user-id-form-content.title.label',
					})}
					id="title"
					name="title"
					value={title.value}
					onValueChange={onChange}
					customValidators={[(value) => onTextInputValidator('title', value)]}
				/>
			</FlexgridItem>
			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<GetCompany
					mode={mode}
					formatMessage={formatMessage}
					company={company}
					onChange={onChange}
					onTextInputValidator={onTextInputValidator}
					valueMissing={valueMissing}
				/>
			</FlexgridItem>
			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<Select
					label={formatMessage({
						id: 'create-user-id-form-content.device-type.label',
					})}
					name="deviceType"
					value={deviceType}
					onValueChange={invokeDeviceTypeChange}
					id="deviceType"
					required={true}
					fieldClassName="wf-col wf-col--8 wf-col--mob-only-8 wf-col--tab-only-8"
				>
					<MenuItem value="landline">
						<FormattedMessage id="create-user-id-form-content.device-type.landline.label" />
					</MenuItem>
					<MenuItem value="cell">
						<FormattedMessage id="create-user-id-form-content.device-type.mobile.label" />
					</MenuItem>
				</Select>
			</FlexgridItem>
			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<TextInput
					aria-label={formatMessage({
						id: 'create-user-id-form-content.phone.aria-label',
					})}
					label={formatMessage({
						id: 'create-user-id-form-content.phone.label',
					})}
					id="phone"
					name="phone"
					value={phone.value}
					onBlur={onBlur}
					onValueChange={onChange}
					aria-required={true}
					required={true}
					ref={phoneRef}
					customValidators={[(value) => onTextInputValidator('phone', value)]}
					nativeValidationMessages={{
						valueMissing,
					}}
				/>
			</FlexgridItem>

			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<TextInput
					aria-label={formatMessage({
						id: 'create-user-id-form-content.extension.aria-label',
					})}
					label={formatMessage({
						id: 'create-user-id-form-content.extension.label',
					})}
					id="extension"
					name="extension"
					value={extension.value}
					disabled={extensionDisabled}
					onBlur={onBlur}
					aria-required={true}
					onValueChange={onChange}
					customValidators={[
						(value) => onTextInputValidator('extension', value),
					]}
				/>
			</FlexgridItem>
			<LanguagePreference
				show={showLanguageDropDown}
				languagePreference={languagePreference}
				invokeLanguagePreferenceChange={invokeLanguagePreferenceChange}
				formatMessage={formatMessage}
			/>
			<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
				<TextInput
					aria-label={formatMessage({
						id: 'create-user-id-form-content.user-id.aria-label',
					})}
					label={formatMessage({
						id: 'create-user-id-form-content.user-id.label',
					})}
					aria-describedby="requirements"
					id="newUserIdNotCC"
					name="newUserIdNotCC"
					value={newUserIdNotCC.value}
					onValueChange={onChange}
					aria-required={true}
					required={true}
					ref={userIdRef}
					customValidators={[
						(value) => onTextInputValidator('newUserIdNotCC', value),
					]}
					nativeValidationMessages={{
						valueMissing,
					}}
				/>
			</FlexgridItem>
		</Flexgrid>
	)
}

export function LanguagePreference({
	show,
	languagePreference,
	invokeLanguagePreferenceChange,
	formatMessage,
}) {
	if (!show) {
		return <Fragment />
	}

	return (
		<FlexgridItem span={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
			<Select
				label={formatMessage({
					id: 'create-user-id-form-content.language-preference.label',
				})}
				name="languagePreference"
				value={languagePreference}
				onValueChange={invokeLanguagePreferenceChange}
				id="languagePreference"
				required={true}
				fieldClassName="wf-col wf-col--8 wf-col--mob-only-8 wf-col--tab-only-8"
			>
				<MenuItem value="English">
					<FormattedMessage id="create-user-id-form-content.language-preference.english.label" />
				</MenuItem>
				<MenuItem value="French">
					<FormattedMessage id="create-user-id-form-content.language-preference.french.label" />
				</MenuItem>
			</Select>
		</FlexgridItem>
	)
}

export function CreateUserIdFormFooter({ formatMessage, onHideCreateUserId }) {
	return (
		<div>
			<Button
				className="CreateUserIdFormFooterSubmitButton"
				kind="primary"
				type="submit"
			>
				{formatMessage({
					id: 'create-user-id-form-footer.submit-button.label',
				})}
			</Button>
			<Button
				className="CreateUserIdFormFooterCancelButton"
				kind="chromeless"
				onClick={() => onHideCreateUserId()}
			>
				{formatMessage({
					id: 'create-user-id-form-footer.cancel-button.label',
				})}
			</Button>
		</div>
	)
}

export function CreateUserIdForm({
	createUserIdMessage,
	inputFields,
	deviceType,
	sbaDetails,
	showCreateUserIdForm,
	showUserIdConfirmationResponse,
	userIdErrorMessage,
	messages,
	formIsValid,
	intl,
	userIdClass,
	mode,
	languagePreference,
	showLanguageDropDown,
	onHideCreateUserId,
	onUpdateInput,
	onSubmitNewUserId,
	onFocusInput,
	onBlurInput,
	onDeviceTypeChange,
	onLanguagePreferenceChange,
	onCustomTextInputValidator,
}) {
	const { formatMessage } = intl
	const formRef = useRef()

	function createNewUserId() {
		if (formIsValid) {
			return onSubmitNewUserId()
		}
		if (
			mode === WFINFOLEASE ||
			mode === PLINFOLEASE ||
			mode === FORDPRO ||
			mode === RICOH
		) {
			formRef.current.validateField('firstName')
			formRef.current.validateField('lastName')
			formRef.current.validateField('deviceType')
			formRef.current.validateField('phone')
			formRef.current.validateField('newUserIdNotCC')
		}
		return onFocusUserIdInput('.UserId #newUserId')
	}

	return (
		<ModalDialog
			isFullScreen={true}
			onClose={onHideCreateUserId}
			open={showCreateUserIdForm}
			title={formatMessage({ id: 'create-user-id-form.title' })}
		>
			<Form action="#" ref={formRef} onSubmit={createNewUserId}>
				<Flexgrid columnGap={2} rowGap={1}>
					<CreateUserIdFormContent
						createUserIdMessage={createUserIdMessage}
						inputFields={inputFields}
						deviceType={deviceType}
						sbaDetails={sbaDetails}
						showUserIdConfirmationResponse={showUserIdConfirmationResponse}
						userIdErrorMessage={userIdErrorMessage}
						messages={messages}
						formatMessage={formatMessage}
						userIdClass={userIdClass}
						mode={mode}
						languagePreference={languagePreference}
						showLanguageDropDown={showLanguageDropDown}
						onUpdateInput={onUpdateInput}
						onFocusInput={onFocusInput}
						onBlurInput={onBlurInput}
						onDeviceTypeChange={onDeviceTypeChange}
						onLanguagePreferenceChange={onLanguagePreferenceChange}
						onCustomTextInputValidator={onCustomTextInputValidator}
					/>
				</Flexgrid>

				<Flexgrid>
					<FlexgridItem span={12} style={{ marginTop: '16px' }}>
						<CreateUserIdFormFooter
							onHideCreateUserId={onHideCreateUserId}
							onSubmitNewUserId={onSubmitNewUserId}
							formIsValid={formIsValid}
							mode={mode}
							formatMessage={formatMessage}
						/>
					</FlexgridItem>
				</Flexgrid>
			</Form>
		</ModalDialog>
	)
}
